import { StickyActionBar, UserCombobox } from '@/components'
import {
  Button,
  CenteredLoader,
  Checkbox,
  Input,
} from '@/components_deprecated'
import type { UserNode } from '@/modules/users'
import type { Coordinates } from '@/types/graphql/maps'
import type { Nullable } from '@/types/util'
import type { FileAttachment } from '@/utils/hooks'
import {
  useAdmin,
  useOnErrorAuto,
  usePermissions,
  useUser,
} from '@/utils/hooks'
import { useGoogleLoader } from '@/utils/hooks/useGoogleLoader'
import { PERMISSIONS } from '@/utils/permissions'
import { useQuery } from '@apollo/client'
import { useToast } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { formatISO } from 'date-fns'
import React, { useState } from 'react'
import { useNavigate } from 'react-router'
import styled from 'styled-components'
import type { ShallowCategory } from '../components'
import {
  Attachments,
  CreateDetails,
  CreateIssueCategoryCard,
  CreateMainInfo,
  IssueRelationsPanel,
  IssueTagPicker,
  Map,
  serializeRelationOptions,
  useRequiredRelationsSatisfied,
} from '../components'
import {
  IssueFormField,
  buildIssueFormValidators,
  omitHiddenIssueInput,
  useIssueFieldConfig,
} from '../field-config'
import { needsLocationGeocode } from '../issue-location'
import { useCreateIssueMutation } from '../mutations.hooks'
import {
  ALL_ISSUE_RISK_ASPECTS_QUERY,
  GET_ALL_ISSUE_SUBCATEGORIES,
} from '../queries'
import type { EditIssueForm } from '../types'
import type {
  AllIssueRiskAspectsQuery,
  AllIssueRiskAspectsQueryVariables,
  CreateIssueInput,
} from '../types.graphl'
import { H2 } from '@/components'

const Wrapper = styled.div`
  ${props => props.theme.layout.default};

  /* Room for the fixed mobile action bar; on desktop the bar is in-flow.
     Must follow layout.default so it wins over its mobile "padding: 1rem". */
  ${props => props.theme.media.mobile} {
    padding-bottom: 8rem;
  }

  .user-select {
    width: 100%;
    max-width: 400px;
  }
`
const Content = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 1rem;

  /* On mobile the two columns flatten into a single stack (display: contents)
     so individual cards can be reordered — the metadata card is lifted to sit
     right after the title (see CardTitle / CreateMainInfo order rules). */
  ${props => props.theme.media.custom({ max: '1100px' })} {
    flex-direction: column;
    align-items: stretch;
  }
`
const MainColumn = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  ${props => props.theme.media.custom({ max: '1100px' })} {
    display: contents;
  }
`
const Rail = styled.aside`
  flex: 0 0 340px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  ${props => props.theme.media.custom({ max: '1100px' })} {
    display: contents;
  }
`
const CardTitle = styled.div`
  padding: 1.25rem 1.5rem;
  border-radius: 16px;
  background: white;
  box-shadow: var(--shadow-card);

  > .inner {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    max-width: 600px;
  }

  h2 {
    margin: 0;
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: #78716c;
  }

  ${props => props.theme.media.custom({ max: '1100px' })} {
    order: -2;
  }
`
const Loading = styled.div`
  position: absolute;
  z-index: 1;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  display: flex;
  justify-content: center;
  align-items: center;

  border-radius: ${props => props.theme.sizes.defaultBorderRadius};
  background-color: rgba(255, 255, 255, 0.5);
`

interface CreateIssueProps {}

export const CreateIssue: React.FC<CreateIssueProps> = () => {
  const translations = useTranslate({
    cancel: 'common.cancel',
    save: 'common.save',

    createIssue: 'issues.create-issue',
    createIssueFor: 'issues.create-issue-for',
    title: 'issues.title',
    extendForm: 'issues.extend-issue-form',

    validation: {
      required: 'common.required',
      couldNotFindLocation: 'error.could-not-find-location',
      makeSureToSelectAnOptionFromTheDropdown:
        'error.make-sure-to-select-an-option-from-the-dropdown',
    },
  })

  const navigate = useNavigate()
  const onErrorAuto = useOnErrorAuto()
  const { scriptLoaded: googleLoaded } = useGoogleLoader()
  const me = useUser()
  const admin = useAdmin()
  const { hasPermissions } = usePermissions()
  const canSubmitForOthers = hasPermissions(
    [PERMISSIONS.issues.view.othersissue, PERMISSIONS.users.view.user],
    true
  )

  // Which fields apply depends on the picked category, which lives in the
  // form below — mirrored here because the hook runs before it exists.
  const [configCategory, setConfigCategory] = useState<string>('')
  const fieldConfig = useIssueFieldConfig(configCategory)
  const addToast = useToast()

  const [coordinates, setCoordinates] = useState<Nullable<Coordinates>>({
    lat: null,
    lng: null,
  })
  // Defaulted from the picked category and shown in the metadata card until
  // the issue is saved, at which point the backend sets them for real.
  const [responsibleUser, setResponsibleUser] = useState<Pick<
    UserNode,
    'id' | 'fullName'
  > | null>(null)
  const [caseHandler, setCaseHandler] = useState<Pick<
    UserNode,
    'id' | 'fullName'
  > | null>(null)
  const [attachments, setAttachments] = useState<FileAttachment[]>([])
  const [manuallyValidateForm, setManuallyValidateForm] = useState(false)
  const [validatedConfigVersion, setValidatedConfigVersion] = useState<string>(
    fieldConfig.version
  )

  const {
    formValues: form,
    formErrors: errors,
    formValid,
    formEdited,
    updateForm,
    validateForm,
    formChangeHandler: handler,
    submitHandler: submit,
  } = useForm<EditIssueForm>({
    values: {
      user: me.id,
      responsibleUser: null,
      caseHandler: null,
      closed: false,
      archived: false,
      category: '',
      subcategory: '',
      date: new Date(),
      dateEnd: new Date(),

      placename: '',
      regNr: null,
      title: '',
      description: '',
      damage: '',
      reason: '',
      locationDescription: '',
      immediateAction: '',
      action: '',

      estimatedCost: 0,
      suggestedSolution: '',

      actualCost: null,
      solution: '',
      severity: null,
      riskAspect: null,
      processChoice: null,
      relationOptions: {},
      tags: [],
    },
    validators: {
      // Which fields are mandatory is a per-company setting; these come from
      // IssueFieldConfig. Only the rules that are not "is it filled in" — the
      // geocode check and the close-out fields — stay hardcoded here.
      ...buildIssueFormValidators(fieldConfig, 'create', {
        requiredMessage: translations.validation.required,
        hasRiskAspects: () => hasRiskAspects,
        hasSubcategories: () => hasSubcategories,
      }),
      placename: val => {
        if (!fieldConfig.isVisible(IssueFormField.LOCATION_PLACENAME, 'create'))
          return null
        if (!val)
          return fieldConfig.isRequired(
            IssueFormField.LOCATION_PLACENAME,
            'create'
          )
            ? translations.validation.required
            : null
        return coordinates.lat && coordinates.lng
          ? null
          : translations.validation.makeSureToSelectAnOptionFromTheDropdown
      },
      solution: (val, other) =>
        other.closed ? (val ? null : translations.validation.required) : null,
    },
    config: {
      initAsInvalid: true,
    },
  })

  const { data: riskAspectData } = useQuery<
    AllIssueRiskAspectsQuery,
    AllIssueRiskAspectsQueryVariables
  >(ALL_ISSUE_RISK_ASPECTS_QUERY)

  const { data: subcategories } = useQuery(GET_ALL_ISSUE_SUBCATEGORIES, {
    variables: { categoryId: form.category ?? '' },
    onError: onErrorAuto(),
  })

  // Annotated to break the inference cycle: the form's validators read
  // these, and they are derived from queries keyed on the form's category.
  const hasSubcategories: boolean =
    !!subcategories?.allIssueSubcategories?.edges?.length

  const hasRiskAspects: boolean =
    (riskAspectData?.allIssueRiskAspects.edges.length ?? 0) > 0

  const mutations = useCreateIssueMutation()
  const relationsValid = useRequiredRelationsSatisfied(
    form.relationOptions,
    'create',
    form.category
  )

  function handleLocationUpdate(
    results: google.maps.GeocoderResult[] | null,
    status: google.maps.GeocoderStatus
  ) {
    if (status !== google.maps.GeocoderStatus.OK || !results) return

    const [place] = results
    if (!place || !place.formatted_address) return

    const [lat, lng] = [
      place.geometry.location.lat(),
      place.geometry.location.lng(),
    ]
    setCoordinates({
      lat,
      lng,
    })

    updateForm({
      placename: place.formatted_address,
    })
    setManuallyValidateForm(true)
  }

  function handleCancel() {
    void navigate('/issues')
  }

  function handleSubmit(values: EditIssueForm) {
    if (!values.category) return

    // Google is only needed to resolve the location field's coordinates, and
    // only when the user actually gave a place we have no coordinates for.
    const needsGeocoding = needsLocationGeocode({
      visible: fieldConfig.isVisible(
        IssueFormField.LOCATION_PLACENAME,
        'create'
      ),
      placename: values.placename,
      coordinates,
    })
    if (needsGeocoding && (typeof google === 'undefined' || !googleLoaded))
      throw new Error('Issue submit: google is undefined')

    function runMutation(input: CreateIssueInput) {
      try {
        void mutations.create({
          variables: { input },
        })
      } catch (error) {
        console.error('Create issue mutation error:', error)
      }
    }

    let input: CreateIssueInput = {
      user: (canSubmitForOthers ? values.user : me.id) ?? me.id,
      category: values.category,
      subcategory: values.subcategory,
      timeOfIssueStart: formatISO(values.date),
      timeOfIssueEnd: formatISO(values.dateEnd),

      vehiclePlateNumber: values.regNr,
      title: values.title,
      issueDescription: values.description,
      damageDescription: values.damage,
      reason: values.reason,
      immediateAction: values.immediateAction,
      action: values.action,
      solutionSuggestion: values.suggestedSolution,
      costEstimation: values.estimatedCost,

      locationPlacename: values.placename,
      locationLatitude: coordinates.lat,
      locationLongitude: coordinates.lng,
      locationDescription: values.locationDescription,

      attachments: attachments.map(attachment => ({
        file: attachment.file,
        name: attachment.name,
      })),

      actualCost: values.closed ? values.actualCost ?? 0 : values.actualCost,
      solution: values.solution,
      closed: values.closed,
      severity: values.severity,
      riskAspect: values.riskAspect,
      processChoice: values.processChoice,
      relationOptions: serializeRelationOptions(values.relationOptions),
      tags: values.tags,
    }

    // A hidden field has no value the user chose — don't send our placeholder.
    input = omitHiddenIssueInput(fieldConfig, 'create', input)

    // Nothing to geocode: the location field is hidden, was left blank, or
    // its coordinates are already resolved.
    if (!needsGeocoding) {
      runMutation(input)
      return
    }

    const service = new google.maps.Geocoder()
    void service.geocode(
      {
        address: values.placename,
      },
      (results, status) => {
        if (status !== google.maps.GeocoderStatus.OK) {
          addToast('error', translations.validation.couldNotFindLocation)
          return
        }

        const [place] = results ?? [null]
        if (!place) {
          runMutation(input)
          return
        }

        const [lat, lng] = [
          place.geometry.location.lat(),
          place.geometry.location.lng(),
        ]
        input = {
          ...input,
          locationLatitude: lat,
          locationLongitude: lng,
        }
        runMutation(input)
      }
    )
  }

  // Cards that hold nothing but hidden fields should not be drawn at all.
  const showTitleCard =
    fieldConfig.isVisible(IssueFormField.TITLE, 'create') ||
    fieldConfig.isVisible(IssueFormField.TAGS, 'create')
  const showMap = fieldConfig.isVisible(
    IssueFormField.LOCATION_PLACENAME,
    'create'
  )

  const isLoading = mutations.loading || fieldConfig.loading

  if (
    manuallyValidateForm &&
    coordinates.lat !== null &&
    coordinates.lng !== null
  ) {
    validateForm()
    setManuallyValidateForm(false)
  }

  function handleChangeCategory(id: string, category: ShallowCategory | null) {
    updateForm({ category: id, subcategory: null })
    setResponsibleUser(category?.responsibleUser ?? null)
    setCaseHandler(category?.caseHandler ?? null)
  }

  if (configCategory !== (form.category ?? '')) {
    setConfigCategory(form.category ?? '')
  }

  // Validators read the config through a ref, so re-run them once it arrives.
  if (validatedConfigVersion !== fieldConfig.version) {
    setValidatedConfigVersion(fieldConfig.version)
    validateForm()
  }

  return (
    <Wrapper>
      <header className="mb-4 flex flex-wrap items-center gap-3">
        <H2>
          {canSubmitForOthers
            ? translations.createIssueFor
            : translations.createIssue}
        </H2>
        {canSubmitForOthers && (
          <UserCombobox
            mode="single"
            className="user-select"
            value={form.user ?? ''}
            clearable={false}
            onChange={value => handler('user')(value || null)}
          />
        )}
      </header>

      <CreateIssueCategoryCard
        category={form.category}
        subcategory={form.subcategory}
        subcategoryOptions={(
          subcategories?.allIssueSubcategories?.edges ?? []
        )?.map(el => ({
          label: el?.node?.name ?? '',
          value: el?.node?.id ?? '',
        }))}
        categoryError={errors.category}
        subcategoryError={errors.subcategory}
        fieldConfig={fieldConfig}
        onChangeCategory={handleChangeCategory}
        onChangeSubcategory={value => handler('subcategory')(value)}
      />

      <Content>
        {isLoading && (
          <Loading>
            <CenteredLoader marginTop="0" size={72} thickness={8} />
          </Loading>
        )}

        <MainColumn>
          {showTitleCard && (
            <CardTitle>
              <div className="inner">
                {fieldConfig.isVisible(IssueFormField.TITLE, 'create') && (
                  <>
                    <h2>
                      {fieldConfig.labelFor(
                        IssueFormField.TITLE,
                        translations.title
                      )}
                      {fieldConfig.isRequired(
                        IssueFormField.TITLE,
                        'create'
                      ) && <span className="ml-1 text-rose-500">*</span>}
                    </h2>
                    <Input
                      value={form.title}
                      error={errors.title}
                      fullWidth
                      grayed
                      height="3rem"
                      onChange={handler('title')}
                    />
                  </>
                )}
                {fieldConfig.isVisible(IssueFormField.TAGS, 'create') && (
                  <div style={{ marginTop: '0.75rem' }}>
                    <IssueTagPicker
                      value={form.tags}
                      onChange={tags => updateForm({ tags })}
                    />
                  </div>
                )}
              </div>
            </CardTitle>
          )}

          <CreateDetails
            form={form}
            errors={errors}
            fieldConfig={fieldConfig}
            onLocationUpdate={handleLocationUpdate}
            onChange={handler}
          />

          {fieldConfig.isVisible(IssueFormField.ATTACHMENTS, 'create') && (
            <Attachments
              editing
              attachments={[]}
              onUpdate={add => setAttachments(add)}
            />
          )}
        </MainColumn>

        <Rail>
          {showMap && (
            <Map
              editing
              latitude={coordinates.lat}
              longitude={coordinates.lng}
              onClick={handleLocationUpdate}
            />
          )}

          <CreateMainInfo
            form={form}
            fieldConfig={fieldConfig}
            hasRiskAspects={hasRiskAspects}
            responsibleUser={responsibleUser}
            caseHandler={caseHandler}
            onChange={handler}
            updateForm={updateForm}
          />

          <IssueRelationsPanel
            value={form.relationOptions}
            editable
            mode="create"
            categoryId={form.category}
            onChange={(relationId, optionIds) =>
              updateForm({
                relationOptions: {
                  ...form.relationOptions,
                  [relationId]: optionIds,
                },
              })
            }
          />
        </Rail>
      </Content>

      <StickyActionBar className="justify-between">
        {admin ? (
          <Checkbox
            checked={form.closed}
            onChange={handler('closed')}
            label={translations.extendForm}
          />
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <span className="hidden md:contents">
            <Button
              disabled={isLoading}
              background="gray8"
              height="2.75rem"
              fontSize="1rem"
              onClick={handleCancel}
            >
              {translations.cancel}
            </Button>
          </span>
          <Button
            disabled={
              isLoading ||
              !formValid ||
              !formEdited ||
              !relationsValid ||
              (hasSubcategories &&
                fieldConfig.isVisible(IssueFormField.SUBCATEGORY, 'create') &&
                fieldConfig.isRequired(IssueFormField.SUBCATEGORY, 'create') &&
                !form.subcategory)
            }
            height="2.75rem"
            fontSize="1rem"
            onClick={submit(handleSubmit)}
          >
            {translations.save}
          </Button>
        </div>
      </StickyActionBar>
    </Wrapper>
  )
}
